'use client';

import type { CSSProperties } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

export type LensPromotionLabel = {
  promotion_label?: string | number | null;
  promotionLabel?: string | number | null;
  short_promotion_label?: string | number | null;
  shortPromotionLabel?: string | number | null;
};

interface SelectLensPromotionTagProps {
  className?: string;
  label?: LensPromotionLabel | null;
}

const PROMOTION_TEXT_SEPARATOR = '\n';

const MEASURE_STYLE: CSSProperties = {
  left: -9999,
  maxWidth: 'none',
  overflow: 'visible',
  pointerEvents: 'none',
  position: 'fixed',
  textOverflow: 'clip',
  top: -9999,
  visibility: 'hidden',
  whiteSpace: 'nowrap',
  width: 'max-content',
  zIndex: -1,
};

const normalizeText = (value: unknown) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return undefined;
  }

  const text = String(value).trim();

  return text.length > 0 ? text : undefined;
};

const getUniqueTexts = (texts: Array<string | undefined>): string[] =>
  Array.from(new Set(texts.filter((text): text is string => Boolean(text))));

export const resolveLensPromotionLabelTexts = (label?: LensPromotionLabel | null): string[] =>
  getUniqueTexts([
    normalizeText(label?.promotionLabel) ?? normalizeText(label?.promotion_label),
    normalizeText(label?.shortPromotionLabel) ?? normalizeText(label?.short_promotion_label),
  ]);

const getCssMaxWidth = (maxWidth: string, parentWidth: number) => {
  if (!maxWidth || maxWidth === 'none') {
    return Number.POSITIVE_INFINITY;
  }

  if (maxWidth.endsWith('%')) {
    const percent = Number.parseFloat(maxWidth);

    return Number.isFinite(percent) ? (parentWidth * percent) / 100 : Number.POSITIVE_INFINITY;
  }

  const width = Number.parseFloat(maxWidth);

  return Number.isFinite(width) ? width : Number.POSITIVE_INFINITY;
};

export default function SelectLensPromotionTag({ className, label }: SelectLensPromotionTagProps) {
  const promotionTexts = useMemo(() => resolveLensPromotionLabelTexts(label), [label]);
  const [fittedText, setFittedText] = useState('');
  const tagRef = useRef<HTMLSpanElement | null>(null);
  const measureRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (promotionTexts.length === 0) {
      setFittedText('');
      return;
    }

    const tagElement = tagRef.current;
    const measureElement = measureRef.current;

    if (!tagElement || !measureElement) {
      setFittedText(promotionTexts[0]);
      return;
    }

    const updateFittedText = () => {
      const parentWidth = tagElement.parentElement?.getBoundingClientRect().width ?? 0;
      const tagWidth = tagElement.getBoundingClientRect().width;
      const computedStyle = window.getComputedStyle(tagElement);
      const cssMaxWidth = getCssMaxWidth(computedStyle.maxWidth, parentWidth);
      const availableWidth = Number.isFinite(cssMaxWidth) ? cssMaxWidth : tagWidth || parentWidth;

      if (!availableWidth) {
        setFittedText(promotionTexts[0]);
        return;
      }

      const nextText =
        promotionTexts.find((text) => {
          measureElement.textContent = text;

          return measureElement.scrollWidth <= Math.ceil(availableWidth);
        }) ?? promotionTexts[promotionTexts.length - 1];

      setFittedText(nextText);
    };

    updateFittedText();

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateFittedText);
    const parentElement = tagElement.parentElement;

    if (resizeObserver) {
      resizeObserver.observe(tagElement);

      if (parentElement) {
        resizeObserver.observe(parentElement);
      }
    }

    window.addEventListener('resize', updateFittedText);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateFittedText);
    };
  }, [promotionTexts]);

  const displayText = fittedText || promotionTexts[0] || '';

  if (!displayText) {
    return null;
  }

  return (
    <>
      <span ref={tagRef} className={className} title={displayText}>
        {displayText}
      </span>
      <span ref={measureRef} aria-hidden="true" className={className} style={MEASURE_STYLE}>
        {promotionTexts.join(PROMOTION_TEXT_SEPARATOR)}
      </span>
    </>
  );
}
